import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { api } from "../../api/api";
import { tr } from "../../i18n";

function ago(iso) {
  const min = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (min < 1) return tr("just now");
  if (min < 60) return tr("{n} min ago", { n: min });
  const h = Math.round(min / 60);
  if (h < 48) return tr("{n} h ago", { n: h });
  return new Date(iso).toLocaleString();
}

/**
 * Latest screenshot from a Notiz Player box, with "Ta skärmbild nu". Until the screen has sent
 * one (plain browsers never do), it says the screenshot is missing.
 */
export default function ScreenshotCard({ screenId, canEdit }) {
  const [at, setAt] = useState(null);
  const [version, setVersion] = useState(0);
  const [big, setBig] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/screen/screenshot/${screenId}/info`);
      setAt(data.at);
      return data.at;
    } catch (e) {
      return null;
    }
  }, [screenId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const requestNow = async () => {
    const before = at;
    setWaiting(true);
    try {
      await api.post(`/screen/screenshot/${screenId}/request`);
      toast.success(tr("Asked the screen for a screenshot"));
    } catch (e) {
      toast.error(tr("Could not reach the screen"));
      setWaiting(false);
      return;
    }
    // The box answers within a few seconds when it is online.
    for (let i = 0; i < 10; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      const now = await load();
      if (now && now !== before) {
        setVersion((v) => v + 1);
        break;
      }
    }
    setWaiting(false);
  };

  const base = String(api.defaults.baseURL || "/api").replace(/\/$/, "");
  const src = `${base}/screen/screenshot/${screenId}?v=${encodeURIComponent(at || "")}-${version}`;

  return (
    <Box>
      {at ? (
        <>
          <img
            src={src}
            alt={tr("Latest screenshot")}
            onClick={() => setBig(true)}
            style={{ width: "100%", borderRadius: 8, cursor: "zoom-in", display: "block", background: "#000" }}
          />
          <Typography sx={{ fontSize: 12, color: "#667085", mt: 1 }}>{tr("Taken {when}", { when: ago(at) })}</Typography>
          <Dialog open={big} onClose={() => setBig(false)} maxWidth="lg">
            <DialogContent sx={{ p: 0 }}>
              <img src={src} alt={tr("Latest screenshot")} style={{ width: "100%", display: "block" }} />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Box sx={{ border: "1px dashed #d0d5dd", borderRadius: 2, p: 3, textAlign: "center", color: "#667085", fontSize: 14 }}>
          {tr("Screenshot missing")}
        </Box>
      )}
      {canEdit && (
        <Button size="small" variant="outlined" disabled={waiting} onClick={requestNow} sx={{ mt: 1, textTransform: "none" }}>
          {waiting ? tr("Waiting for the screen…") : tr("Take screenshot now")}
        </Button>
      )}
    </Box>
  );
}
